// sections/WebChatSection.jsx
// Web Channel management — embed snippet, widget key, appearance, company name, campaign

import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Globe,
  Copy,
  RefreshCw,
  Check,
  Loader2,
  AlertCircle,
  CheckCircle,
  Info,
  Code2,
  Tag,
  Palette,
  ShieldAlert,
  Zap,
} from 'lucide-react';
import WebChatInstallGuide from './WebChatInstallGuide';
import WebChatExplainerModal from '../../../../pages/campaign-console/WebChatExplainerModal';
import { useAuth } from '../../../../context/AuthContext';
import supabase from '../../../../lib/supabaseClient';

// ============================================================================
// HELPERS
// ============================================================================

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  if (process.env.NODE_ENV === 'development') return 'http://localhost:3001';
  return 'https://api.getsurfox.com/api';
};

const makeAuthenticatedRequest = async (endpoint, options = {}) => {
  const { data: { session }, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) throw new Error(`Authentication error: ${sessionError.message}`);
  if (!session?.access_token) throw new Error('No valid session. Please log in again.');

  const response = await fetch(`${getApiBaseUrl()}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
      ...options.headers,
    },
  });

  if (!response.ok) {
    let msg = `${response.status} ${response.statusText}`;
    try { const e = await response.json(); msg = e.error || e.message || msg; } catch {}
    throw new Error(msg);
  }

  const text = await response.text();
  return text ? JSON.parse(text) : {};
};

const COLOR_PRESETS = [
  { label: 'Indigo',   value: '#6366f1' },
  { label: 'Orange',   value: '#f97316' },
  { label: 'Emerald',  value: '#10b981' },
  { label: 'Sky',      value: '#0ea5e9' },
  { label: 'Rose',     value: '#f43f5e' },
  { label: 'Violet',   value: '#8b5cf6' },
  { label: 'Slate',    value: '#475569' },
  { label: 'Custom',   value: 'custom'  },
];

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

const SectionCard = ({ icon: Icon, iconColor, title, subtitle, children }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5">
    <div className="flex items-center space-x-3 mb-5">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br ${iconColor}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div>
        <h4 className="font-semibold text-slate-900">{title}</h4>
        {subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}
      </div>
    </div>
    {children}
  </div>
);

const CopyButton = ({ value }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button
      onClick={handleCopy}
      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg
                 text-slate-600 bg-slate-100 hover:bg-slate-200 transition-colors flex-shrink-0"
    >
      {copied ? (
        <><Check className="w-3.5 h-3.5 text-emerald-600" /><span className="text-emerald-600">Copied</span></>
      ) : (
        <><Copy className="w-3.5 h-3.5" /><span>Copy</span></>
      )}
    </button>
  );
};

// Mini live preview of the widget bubble + panel header
const WidgetPreview = ({ color, headerText, position }) => (
  <div className="relative bg-slate-100 rounded-xl border border-slate-200 h-40 overflow-hidden select-none">
    <div className="absolute inset-0 flex items-end justify-end p-3" style={{ justifyContent: position === 'left' ? 'flex-start' : 'flex-end' }}>
      {/* Chat bubble */}
      <div
        className="w-11 h-11 rounded-full flex items-center justify-center shadow-lg cursor-pointer flex-shrink-0"
        style={{ backgroundColor: color }}
      >
        <svg viewBox="0 0 24 24" className="w-6 h-6 fill-white">
          <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>
        </svg>
      </div>
    </div>

    {/* Mini panel preview */}
    <div
      className="absolute bottom-16 rounded-xl shadow-xl overflow-hidden"
      style={{
        width: 160,
        right: position === 'left' ? 'auto' : 12,
        left:  position === 'left' ? 12 : 'auto',
      }}
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2" style={{ backgroundColor: color }}>
        <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs">💬</div>
        <span className="text-white text-xs font-semibold truncate flex-1">{headerText || 'Chat with us'}</span>
        <div className="w-2 h-2 rounded-full bg-green-400 flex-shrink-0" />
      </div>
      {/* Fake message */}
      <div className="bg-white px-3 py-2 space-y-1.5">
        <div className="bg-slate-100 rounded-xl rounded-bl-sm px-2 py-1.5 text-[9px] text-slate-600 w-28">
          Hey! How can I help?
        </div>
      </div>
    </div>

    <p className="absolute top-2 left-0 right-0 text-center text-[10px] text-slate-400 font-medium">Preview</p>
  </div>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function WebChatSection({ onUpdate }) {
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [regenLoading, setRegenLoading] = useState(false);
  const [regenConfirm, setRegenConfirm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Widget identity
  const [widgetKey, setWidgetKey] = useState('');
  const [embedSnippet, setEmbedSnippet] = useState('');

  // Configuration
  const [companyName, setCompanyName]   = useState('');
  const [campaignId, setCampaignId]     = useState('');
  const [campaigns, setCampaigns]       = useState([]);
  // A campaign picked in the dropdown, waiting on the explainer (same modal the
  // Campaigns page shows before a campaign becomes the web chat campaign).
  const [pendingCampaignId, setPendingCampaignId] = useState(null);

  // Appearance
  const [primaryColor, setPrimaryColor] = useState('#6366f1');
  const [customColor, setCustomColor]   = useState('#6366f1');
  const [headerText, setHeaderText]     = useState('Chat with us');
  const [position, setPosition]         = useState('right');
  const [isCustomColor, setIsCustomColor] = useState(false);

  // Behavior
  const [proactiveEnabled, setProactiveEnabled] = useState(true);
  const [proactiveDelay, setProactiveDelay]     = useState(8);
  const [autoOpenPaths, setAutoOpenPaths]       = useState('/pricing');
  const [pulseEnabled, setPulseEnabled]         = useState(true);

  // Originals for dirty tracking
  const [orig, setOrig] = useState({});

  // Web chat kill switch (2026-09-10) — set once tenants.web_chat_limit_reached_at is
  // stamped, so the settings page can tell the tenant WHY chat is off instead of just
  // showing the generic "campaign is paused" notice for a paused-by-limit campaign.
  const [webChatLimitReachedAt, setWebChatLimitReachedAt] = useState(null);

  const isDirty =
    companyName      !== orig.companyName      ||
    campaignId       !== orig.campaignId       ||
    primaryColor     !== orig.primaryColor     ||
    headerText       !== orig.headerText       ||
    position         !== orig.position         ||
    proactiveEnabled !== orig.proactiveEnabled ||
    proactiveDelay   !== orig.proactiveDelay   ||
    autoOpenPaths    !== orig.autoOpenPaths    ||
    pulseEnabled     !== orig.pulseEnabled;

  // ── Fetch widget config ─────────────────────────────────────────────────────
  const fetchWidgetConfig = useCallback(async () => {
    if (!user?.tenant_id) return;
    setLoading(true);
    setError('');
    try {
      const data = await makeAuthenticatedRequest('/web-chat/widget');
      const color = data.primary_color || '#6366f1';
      const isPreset = COLOR_PRESETS.slice(0, -1).some(p => p.value === color);

      setWidgetKey(data.widget_key || '');
      setEmbedSnippet(data.embed_snippet || '');
      setCompanyName(data.web_chat_company_name || '');
      setCampaignId(data.widget_campaign_id || '');
      setPrimaryColor(color);
      setCustomColor(color);
      setIsCustomColor(!isPreset);
      setHeaderText(data.header_text || 'Chat with us');
      setPosition(data.position || 'right');
      const pEnabled = data.proactive_enabled !== false;
      const pDelay   = data.proactive_delay   || 8;
      // /web-chat/widget returns the saved comma-separated STRING; /web-chat/config returns a list.
      const rawPaths = data.auto_open_paths;
      const pPaths   = (Array.isArray(rawPaths) ? rawPaths.join(', ') : String(rawPaths || '')) || '/pricing';
      const pulse    = data.pulse_enabled     !== false;
      setProactiveEnabled(pEnabled);
      setProactiveDelay(pDelay);
      setAutoOpenPaths(pPaths);
      setPulseEnabled(pulse);
      setOrig({
        companyName:      data.web_chat_company_name || '',
        campaignId:       data.widget_campaign_id    || '',
        primaryColor:     color,
        headerText:       data.header_text           || 'Chat with us',
        position:         data.position              || 'right',
        proactiveEnabled: pEnabled,
        proactiveDelay:   pDelay,
        autoOpenPaths:    pPaths,
        pulseEnabled:     pulse,
      });
    } catch (err) {
      setError(`Failed to load widget config: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [user?.tenant_id]);

  // ── Fetch campaigns ─────────────────────────────────────────────────────────
  const fetchCampaigns = useCallback(async () => {
    if (!user?.tenant_id) return;
    try {
      // Every campaign that is not archived. This used to filter is_active=true, which
      // hid paused and pre-f466e64f campaigns (is_active now means only "not archived",
      // but older paused rows still carry false). The server refuses archived ones too.
      const { data, error } = await supabase
        .from('campaigns')
        .select('id, name, ai_on')
        .eq('tenant_id', user.tenant_id)
        .not('archived', 'is', true)
        .order('name');
      if (error) throw error;
      setCampaigns(data || []);
    } catch (err) {
      console.error('Failed to fetch campaigns:', err.message);
    }
  }, [user?.tenant_id]);

  useEffect(() => {
    fetchWidgetConfig();
    fetchCampaigns();
  }, [fetchWidgetConfig, fetchCampaigns]);

  useEffect(() => {
    if (!user?.tenant_id) return;
    let cancelled = false;
    makeAuthenticatedRequest('/billing/usage')
      .then((u) => { if (!cancelled) setWebChatLimitReachedAt(u.web_chat_limit_reached_at || null); })
      .catch(() => {}); // non-fatal: notice just won't distinguish limit from a manual pause
    return () => { cancelled = true; };
  }, [user?.tenant_id]);

  useEffect(() => {
    if (error || success) {
      const t = setTimeout(() => { setError(''); setSuccess(''); }, 5000);
      return () => clearTimeout(t);
    }
  }, [error, success]);

  // ── Color helpers ───────────────────────────────────────────────────────────
  const handlePresetClick = (preset) => {
    if (preset.value === 'custom') {
      setIsCustomColor(true);
      setPrimaryColor(customColor);
    } else {
      setIsCustomColor(false);
      setPrimaryColor(preset.value);
    }
  };

  const handleCustomColorChange = (val) => {
    setCustomColor(val);
    setPrimaryColor(val);
  };

  // ── Save all settings ───────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const [, campaignResult] = await Promise.all([
        makeAuthenticatedRequest('/web-chat/widget/name', {
          method: 'PATCH',
          body: JSON.stringify({ company_name: companyName }),
        }),
        makeAuthenticatedRequest('/web-chat/widget/campaign', {
          method: 'PATCH',
          body: JSON.stringify({ campaign_id: campaignId || null }),
        }),
        makeAuthenticatedRequest('/web-chat/widget/appearance', {
          method: 'PATCH',
          body: JSON.stringify({ primary_color: primaryColor, header_text: headerText, position }),
        }),
        makeAuthenticatedRequest('/web-chat/widget/behavior', {
          method: 'PATCH',
          body: JSON.stringify({
            proactive_enabled: proactiveEnabled,
            proactive_delay:   proactiveDelay,
            auto_open_paths:   autoOpenPaths,
            pulse_enabled:     pulseEnabled,
          }),
        }),
      ]);
      setOrig({ companyName, campaignId, primaryColor, headerText, position, proactiveEnabled, proactiveDelay, autoOpenPaths, pulseEnabled });
      // Rebuild snippet locally
      const apiUrl = 'https://surfox.ai';
      const attrs = [
        `data-widget-key="${widgetKey}"`,
        companyName       ? `data-company-name="${companyName}"` : '',
        primaryColor !== '#6366f1'      ? `data-primary-color="${primaryColor}"` : '',
        headerText   !== 'Chat with us' ? `data-header-text="${headerText}"`     : '',
        position     !== 'right'        ? `data-position="${position}"`          : '',
      ].filter(Boolean).join(' ');
      setEmbedSnippet(`<script src="${apiUrl}/chat-widget.js" ${attrs}></script>`);
      // The server pauses whichever campaign lost the web chat slot (Decision A).
      const paused = campaignResult?.paused_campaign;
      setSuccess(paused?.was_live ? `Settings saved. ${paused.name} was paused.` : 'Settings saved!');
      if (paused?.id) setCampaigns(prev => prev.map(c => (c.id === paused.id ? { ...c, ai_on: false } : c)));
      onUpdate?.();
    } catch (err) {
      setError(`Save failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // ── Regen widget key ────────────────────────────────────────────────────────
  const handleRegen = async () => {
    if (!regenConfirm) { setRegenConfirm(true); return; }
    setRegenLoading(true);
    setRegenConfirm(false);
    setError('');
    try {
      const data = await makeAuthenticatedRequest('/web-chat/widget/regen', { method: 'POST' });
      setWidgetKey(data.widget_key);
      const attrs = [
        `data-widget-key="${data.widget_key}"`,
        companyName       ? `data-company-name="${companyName}"` : '',
        primaryColor !== '#6366f1'      ? `data-primary-color="${primaryColor}"` : '',
        headerText   !== 'Chat with us' ? `data-header-text="${headerText}"`     : '',
        position     !== 'right'        ? `data-position="${position}"`          : '',
      ].filter(Boolean).join(' ');
      setEmbedSnippet(`<script src="https://surfox.ai/chat-widget.js" ${attrs}></script>`);
      setSuccess('Widget key regenerated. Update the snippet on your website.');
      onUpdate?.();
    } catch (err) {
      setError(`Regen failed: ${err.message}`);
    } finally {
      setRegenLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* Banners */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center space-x-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <span className="text-sm text-red-700">{error}</span>
        </div>
      )}
      {success && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center space-x-3">
          <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
          <span className="text-sm text-emerald-700 font-medium">{success}</span>
        </div>
      )}

      {/* ── 1. Install Guide ── */}
      <SectionCard
        icon={Code2}
        iconColor="from-indigo-500 to-violet-600"
        title="Install Widget"
        subtitle="Deploy to your website using your preferred method"
      >
        <WebChatInstallGuide embedSnippet={embedSnippet} />
      </SectionCard>

      {/* ── 2. Appearance ── */}
      <SectionCard
        icon={Palette}
        iconColor="from-pink-500 to-rose-600"
        title="Appearance"
        subtitle="Brand the widget to match your website"
      >
        <div className="space-y-6">

          {/* Live preview + controls side by side on md+ */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

            {/* Left: controls */}
            <div className="space-y-5">

              {/* Primary color */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Primary color</label>
                <div className="grid grid-cols-4 gap-2">
                  {COLOR_PRESETS.map((preset) => {
                    const isSelected = preset.value === 'custom'
                      ? isCustomColor
                      : !isCustomColor && primaryColor === preset.value;
                    return (
                      <button
                        key={preset.value}
                        onClick={() => handlePresetClick(preset)}
                        title={preset.label}
                        className={`h-9 rounded-lg border-2 transition-all flex items-center justify-center text-xs font-medium
                          ${isSelected ? 'border-slate-900 ring-2 ring-slate-900/10 scale-105' : 'border-slate-200 hover:border-slate-400'}
                        `}
                        style={preset.value !== 'custom' ? { backgroundColor: preset.value } : {
                          background: 'linear-gradient(135deg, #f43f5e, #8b5cf6, #0ea5e9)',
                        }}
                      >
                        {isSelected && (
                          <Check className="w-3.5 h-3.5 text-white drop-shadow" />
                        )}
                      </button>
                    );
                  })}
                </div>
                {isCustomColor && (
                  <div className="flex items-center gap-3 mt-3">
                    <input
                      type="color"
                      value={customColor}
                      onChange={(e) => handleCustomColorChange(e.target.value)}
                      className="w-10 h-10 rounded-lg border border-slate-200 cursor-pointer p-0.5 bg-white"
                    />
                    <input
                      type="text"
                      value={customColor}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (/^#[0-9a-fA-F]{0,6}$/.test(v)) handleCustomColorChange(v);
                      }}
                      maxLength={7}
                      placeholder="#6366f1"
                      className="w-28 px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono
                                 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                    />
                  </div>
                )}
              </div>

              {/* Header text */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Header text</label>
                <input
                  type="text"
                  value={headerText}
                  onChange={(e) => setHeaderText(e.target.value)}
                  maxLength={40}
                  placeholder="Chat with us"
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm
                             focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                />
              </div>

              {/* Position */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Position</label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: 'right', label: 'Bottom right' },
                    { value: 'left',  label: 'Bottom left'  },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setPosition(opt.value)}
                      className={`p-3 rounded-xl border-2 text-sm font-medium transition-all text-center
                        ${position === opt.value
                          ? 'border-orange-500 bg-orange-50 text-orange-700'
                          : 'border-slate-200 text-slate-600 hover:border-slate-300'
                        }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: live preview */}
            <div>
              <p className="text-sm font-medium text-slate-700 mb-2">Live preview</p>
              <WidgetPreview color={primaryColor} headerText={headerText} position={position} />
            </div>
          </div>
        </div>
      </SectionCard>

      {/* ── 3. Behavior ── */}
      <SectionCard
        icon={Zap}
        iconColor="from-amber-500 to-orange-500"
        title="Engagement Behavior"
        subtitle="How the widget proactively reaches visitors"
      >
        <div className="space-y-5">

          {/* Attention pulse */}
          <div className="flex items-center justify-between p-4 bg-slate-50 border border-slate-200 rounded-xl">
            <div>
              <p className="font-medium text-slate-900 text-sm">Attention pulse</p>
              <p className="text-xs text-slate-500 mt-0.5">Bubble bounces + shows a badge to draw the eye — starts immediately on page load</p>
            </div>
            <label className="flex items-center gap-2 cursor-pointer flex-shrink-0 ml-4">
              <span className="text-xs text-slate-500">{pulseEnabled ? 'On' : 'Off'}</span>
              <div className="relative" onClick={() => setPulseEnabled(v => !v)}>
                <div className={`w-10 h-6 rounded-full transition-colors ${pulseEnabled ? 'bg-emerald-500' : 'bg-slate-300'}`}>
                  <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${pulseEnabled ? 'translate-x-5' : 'translate-x-1'}`} />
                </div>
              </div>
            </label>
          </div>

          {/* Proactive notification */}
          <div className="border border-slate-200 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between p-4 bg-slate-50">
              <div>
                <p className="font-medium text-slate-900 text-sm">Proactive notification</p>
                <p className="text-xs text-slate-500 mt-0.5">Pop a greeting card above the bubble after a delay — on all pages</p>
              </div>
              <label className="flex items-center gap-2 cursor-pointer flex-shrink-0 ml-4">
                <span className="text-xs text-slate-500">{proactiveEnabled ? 'On' : 'Off'}</span>
                <div className="relative" onClick={() => setProactiveEnabled(v => !v)}>
                  <div className={`w-10 h-6 rounded-full transition-colors ${proactiveEnabled ? 'bg-emerald-500' : 'bg-slate-300'}`}>
                    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${proactiveEnabled ? 'translate-x-5' : 'translate-x-1'}`} />
                  </div>
                </div>
              </label>
            </div>
            {proactiveEnabled && (
              <div className="px-4 pb-4 pt-3 border-t border-slate-100 bg-white">
                <label className="block text-xs font-medium text-slate-600 mb-1.5">Delay before appearing</label>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min={3}
                    max={30}
                    value={proactiveDelay}
                    onChange={e => setProactiveDelay(Number(e.target.value))}
                    className="flex-1 accent-orange-500"
                  />
                  <span className="text-sm font-semibold text-slate-900 w-16 text-right">
                    {proactiveDelay}s
                  </span>
                </div>
                <div className="flex justify-between text-[10px] text-slate-400 mt-0.5">
                  <span>3s (eager)</span>
                  <span>30s (patient)</span>
                </div>
              </div>
            )}
          </div>

          {/* Auto-open paths */}
          <div className="border border-slate-200 rounded-xl overflow-hidden">
            <div className="p-4 bg-slate-50">
              <p className="font-medium text-slate-900 text-sm">Auto-open on high-intent pages</p>
              <p className="text-xs text-slate-500 mt-0.5">Widget opens fully after the delay on pages matching these paths</p>
            </div>
            <div className="px-4 pb-4 pt-3 border-t border-slate-100 bg-white space-y-2">
              <input
                type="text"
                value={autoOpenPaths}
                onChange={e => setAutoOpenPaths(e.target.value)}
                placeholder="/pricing, /contact, /demo"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono
                           focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
              />
              <p className="text-xs text-slate-400">
                Comma-separated paths. Matched from the start — <code className="bg-slate-100 px-1 rounded">/pricing</code> matches <code className="bg-slate-100 px-1 rounded">/pricing/annual</code> too.
                Leave blank to disable auto-open.
              </p>
            </div>
          </div>

          {/* Info callout */}
          <div className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-100 rounded-xl">
            <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-blue-700 leading-relaxed">
              On auto-open pages the proactive notification is skipped — the panel opens directly. Pulse and notification are session-aware and won't re-trigger once a visitor has engaged.
            </p>
          </div>
        </div>
      </SectionCard>

      {/* ── 4. Configuration ── */}
      <SectionCard
        icon={Globe}
        iconColor="from-cyan-500 to-blue-600"
        title="Configuration"
        subtitle="Greeting and lead routing"
      >
        <div className="space-y-5">
          {/* Company Name */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Company display name</label>
            <input
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="e.g. Acme Staffing"
              className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm
                         focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
            />
            <p className="text-xs text-slate-400 mt-1.5">
              Opening greeting: "Hey! What brought you to <em>{companyName || 'your company'}</em> today?"
            </p>
          </div>

          {/* Campaign */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Default campaign for web chat leads</label>
            <select
              value={campaignId}
              onChange={(e) => {
                const next = e.target.value;
                // Picking a campaign that is not already the web one explains what that
                // does first, exactly as the Campaigns page does.
                if (next && next !== orig.campaignId) setPendingCampaignId(next);
                else setCampaignId(next);
              }}
              className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm bg-white
                         focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
            >
              <option value="">No campaign (unassigned)</option>
              {campaigns.map((c) => (
                <option key={c.id} value={c.id}>{c.name}{c.ai_on === false ? ' (paused)' : ''}</option>
              ))}
            </select>
            <p className="text-xs text-slate-400 mt-1.5">
              New leads captured via the widget are enrolled in this campaign automatically. The chat shows on
              your website only while this campaign is live: with no campaign, or a paused or archived one, it disappears.
            </p>
            {/* Phase 1e (Tom, 2026-09-10): the widget exists only while its campaign is live. */}
            {campaignId && campaigns.find(c => c.id === campaignId)?.ai_on === false && (
              webChatLimitReachedAt ? (
                <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-900">
                  This month's website chat limit has been reached, so there is no chat on your website.
                  It turns back on when your billing cycle resets, or right away if you upgrade to a plan
                  with more capacity.
                </p>
              ) : (
                <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-900">
                  This campaign is paused, so there is no chat on your website until you turn it on.
                </p>
              )
            )}
            {/* Decision A (2026-09-10): the server pauses the campaign that loses the slot. */}
            {orig.campaignId && campaignId !== orig.campaignId && (
              <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-900">
                <strong className="font-semibold">{campaigns.find(c => c.id === orig.campaignId)?.name || 'The current web chat campaign'}</strong> will
                be paused when you save. It will not text anyone until you turn it on again and it passes the SMS checks.
                {!campaignId && ' With no campaign picked, the chat disappears from your website.'}
              </p>
            )}
            {pendingCampaignId && (
              <WebChatExplainerModal
                campaign={campaigns.find(c => c.id === pendingCampaignId) || { name: 'this campaign' }}
                currentHolderName={orig.campaignId ? (campaigns.find(c => c.id === orig.campaignId)?.name || 'your current web chat campaign') : null}
                showSaveHint={false}
                cancelLabel="Keep the current campaign"
                onCancel={() => setPendingCampaignId(null)}
                onConfirm={() => { setCampaignId(pendingCampaignId); setPendingCampaignId(null); }}
              />
            )}
          </div>
        </div>
      </SectionCard>

      {/* ── 4. Widget Key ── */}
      <SectionCard
        icon={Tag}
        iconColor="from-slate-600 to-slate-700"
        title="Widget Key"
        subtitle="Unique identifier included in the embed snippet"
      >
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-xl">
            <code className="flex-1 text-sm font-mono text-slate-800 truncate">{widgetKey}</code>
            <CopyButton value={widgetKey} />
          </div>

          <div className="flex items-center justify-between p-4 bg-red-50 border border-red-200 rounded-xl">
            <div className="flex items-center space-x-3">
              <ShieldAlert className="w-5 h-5 text-red-500 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-red-800">Regenerate Key</p>
                <p className="text-xs text-red-600">
                  {regenConfirm
                    ? 'Old key stops working immediately — confirm to proceed.'
                    : 'Breaks the existing embed. You must update your website.'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {regenConfirm && (
                <button
                  onClick={() => setRegenConfirm(false)}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg text-slate-600 bg-white border border-slate-300 hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
              )}
              <motion.button
                onClick={handleRegen}
                disabled={regenLoading}
                whileTap={{ scale: 0.97 }}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors
                  ${regenConfirm ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-white text-red-600 border border-red-300 hover:bg-red-50'}
                  ${regenLoading ? 'opacity-50 cursor-not-allowed' : ''}
                `}
              >
                {regenLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                {regenConfirm ? 'Yes, Regenerate' : 'Regenerate'}
              </motion.button>
            </div>
          </div>
        </div>
      </SectionCard>

      {/* Save */}
      {isDirty && (
        <div className="flex items-center justify-end pt-2">
          <motion.button
            onClick={handleSave}
            disabled={saving}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            whileTap={{ scale: 0.98 }}
            className={`
              px-6 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200
              flex items-center space-x-2 shadow-sm hover:shadow
              ${success ? 'bg-emerald-500 text-white' : 'bg-slate-900 text-white hover:bg-slate-800'}
              ${saving ? 'opacity-50 cursor-not-allowed' : ''}
            `}
          >
            {saving ? (
              <><Loader2 className="w-4 h-4 animate-spin" /><span>Saving…</span></>
            ) : success ? (
              <><Check className="w-4 h-4" /><span>Saved</span></>
            ) : (
              <span>Save Changes</span>
            )}
          </motion.button>
        </div>
      )}
    </div>
  );
}
